import { useEffect, useState } from "react";
import styles from "./ForgotPasword.module.scss";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { passwordValidation } from "../../helper/auth";
import { AuthService } from "../../services/auth.service";
import { IForgotPasswordModel } from "../../models/User";
import { Form } from "antd";
import FormItemInput from "../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import FormItemInputPassword from "../../components/ui/FormItemInputPassword/FormItemInputPassword";
import { getLanguageFromLocal } from "../../helper/language";
import { openErrorMessage, openSuccessMessage } from "../../store/message/messageSlice";
import useAppDispatch from "../../hooks/useAppDispatch";
import { handleError } from "../../helper/errorHandler";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<1 | 2>(1);
  const [token, setToken] = useState<string>("");
  // "set" mode = a social user creating a first password (worded differently from a reset).
  const [isSetMode, setIsSetMode] = useState<boolean>(false);

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get("otp-token");
      if (searchParams.get("mode") === "set") {
        setIsSetMode(true);
      }
      if (token) {
        navigate("/forgot-password");
        setToken(token);
        setStep(2);
      }
    }
  }, [location]);

  const onFinishStep1 = async (values: { email: string }) => {
    const model = {
      email: values.email.trim(),
      lang: getLanguageFromLocal(),
    };

    try {
      const response = await AuthService.sendForgotPasswordMail(model);

      if (response.status === 200 && response.data) {
        dispatch(openSuccessMessage(t(response.data.message)));
        navigate(
          `/success?title=${t("CheckYourEmail")}&description=${t("DescriptionCheckYourEmail", { email: model.email })}`
        );
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const onFinishStep2 = async (values: IForgotPasswordModel) => {
    const model = {
      newPassword: values.newPassword.trim(),
      confirmNewPassword: values.confirmNewPassword.trim(),
    };

    if (!passwordValidation(model.newPassword)) {
      dispatch(openErrorMessage(t("ValidationPassword")));
      return;
    }

    if (model.newPassword !== model.confirmNewPassword) {
      dispatch(openErrorMessage(t("PASSWORDS_ARE_NOT_EQUAL")));
      return;
    }

    try {
      const response = await AuthService.forgotPassword(token, model);
      if (response.status === 200 && response.data) {
        navigate(
          `/success?title=${isSetMode ? t("PasswordSuccessfullySet") : t("PasswordSuccessfullyChanged")}&description=${t("YourPasswordForClubPassUpdatedSuccessfully")}`
        );
      }
    } catch (e: any) {
      handleError(e, dispatch, t);
      setStep(1);
    }
  };

  return (
    <>
      <div className={"container"}>
        <div className={styles.container}>
          <div className={styles.box}>
            {step === 1 && (
              <>
                <h2 className={styles.title}>
                  {isSetMode ? t("SetYourPassword") : t("ResetYourPassword")}
                </h2>
                <p className={styles.text}>
                  {isSetMode
                    ? t("EnterEmailToSetPassword")
                    : t("EnterEmailAddressUsedToCreateAccount")}
                </p>
                <Form
                  className={styles.form}
                  name="email"
                  initialValues={{ remember: true }}
                  onFinish={onFinishStep1}
                  autoComplete="off"
                  layout={"vertical"}
                >
                  <FormItemInput
                    name={"email"}
                    label={t("EmailAddress")}
                    rules={{ required: true }}
                  />

                  <div className={styles.btnWrapper}>
                    <CustomButton text={t("Submit")} type={"submit"} typeStyle={"fill"} />
                  </div>
                </Form>
              </>
            )}
            {step === 2 && (
              <>
                <h2 className={styles.title}>
                  {isSetMode ? t("CreateYourPassword") : t("ResetYourPassword")}
                </h2>
                <p className={styles.text}>
                  {isSetMode
                    ? t("CreatePasswordForYourAccount")
                    : t("EnterNewPasswordForYourAccount")}
                </p>
                <Form
                  className={styles.form}
                  name="forgot-password"
                  initialValues={{ remember: true }}
                  onFinish={onFinishStep2}
                  autoComplete="off"
                  layout={"vertical"}
                >
                  <FormItemInputPassword
                    name={"newPassword"}
                    label={t("NewPassword")}
                    rules={{ required: true }}
                  />

                  <FormItemInputPassword
                    name={"confirmNewPassword"}
                    label={t("ConfirmNewPassword")}
                    rules={{ required: true }}
                  />

                  <div className={styles.btnWrapper}>
                    <CustomButton text={t("Submit")} type={"submit"} typeStyle={"fill"} />
                  </div>
                </Form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
